import { SegmentSelect } from '@/design-system/components/buttons/segmentSelect/SegmentSelect';
import { Dropdown } from '@/design-system/components/dropdown';
import { Icon } from '@/design-system/components/icons/Icon';
import { TextBody, TextCaption, TextSmall, TextTitle } from '@/design-system/components/typography';
import { DatePickerType, IntervalBillPayment, ScheduleBillPayment } from '@/features/bill-payment/enum';
import { UseBillPaymentFormProp } from '@/features/bill-payment/hooks/useBillPaymentForm.hook';
import { SxProp, useDripsyTheme, View } from 'dripsy';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useWindowDimensions } from 'react-native';
import { DatePickerCallBackComponent } from '../DatePickerCallBack.component';

interface ScheduleComponentMobileProps {
  sx?: SxProp;
  billPaymentProp: UseBillPaymentFormProp;
  onDateClick: (input: DatePickerType) => void;
}

export const ScheduleComponentMobileComponent: React.FC<ScheduleComponentMobileProps> = ({ sx, billPaymentProp, onDateClick }) => {
  const {
    scheduleForm: { scheduleTab, schedule, dateOfFirstPayment, interval, paymentEndDate, setScheduleTab, setInterval },
    general: { billPaymentState }
  } = billPaymentProp;
  const changeTab = (value: string) => {
    setScheduleTab(value);
  };
  const isErrorScheduleDate = billPaymentState && billPaymentState.scheduleDate && billPaymentState.scheduleDate.error;
  const isErrorDateOfFirstPayment = billPaymentState && billPaymentState.dateOfFirstPayment && billPaymentState.dateOfFirstPayment.error;
  const isErrorInterval = billPaymentState && billPaymentState.interval && billPaymentState.interval.error;
  const isErrorPaymentEndDate = billPaymentState && billPaymentState.paymentEndDate && billPaymentState.paymentEndDate.error;

  const { theme } = useDripsyTheme();
  const { width } = useWindowDimensions();
  const { $2, $4 } = theme.space || ({} as any);
  const widthItemTab = (width - ($4 as number) * 2 - ($2 as number)) / 3;
  const { t } = useTranslation('global-bill-payment');

  return (
    <View sx={{ ...sxStyles.container, ...sx }}>
      <View sx={sxStyles.leftContainer}>
        <View sx={sxStyles.number}>
          <TextBody sx={sxStyles.textNumber}>3</TextBody>
        </View>
        <TextTitle semiBold={true} sx={sxStyles.textQuestion}>
          {t('global-bill-payment::payment-details.title--schedule-your-payment')}
        </TextTitle>
      </View>
      <View sx={sxStyles.rightContainer}>
        <View sx={{ alignItems: 'flex-start' }}>
          <SegmentSelect
            key="schedule-header-suffix-0"
            data={[
              {
                labelKey: t('global-bill-payment::glossary.now'),
                value: ScheduleBillPayment.NOW
              },
              {
                labelKey: t('global-bill-payment::glossary.later'),
                value: ScheduleBillPayment.FUTURE
              },
              {
                labelKey: t('global-bill-payment::glossary.recurring'),
                value: ScheduleBillPayment.RECURRING
              }
            ]}
            onValueChange={changeTab}
            buttonWidth={widthItemTab}
          />
        </View>
        {/* Now */}
        {scheduleTab === ScheduleBillPayment.NOW && (
          <View sx={{ mt: '$5' }}>
            <View sx={sxStyles.nowWrapper}>
              <View sx={sxStyles.viewRow}>
                <TextSmall sx={sxStyles.textNumberRecurring}>1.</TextSmall>
                <View sx={{ ...sxStyles.viewColumn, ...{ flex: 1 } }}>
                  <TextSmall sx={{ ...sxStyles.textNote, ...sxStyles.textNoteMargin }}>
                    {t('global-bill-payment::instruction.text--notice-payment-instruction-will')}
                  </TextSmall>
                </View>
              </View>
              <View sx={sxStyles.viewRow}>
                <TextSmall sx={sxStyles.textNumberRecurring}>2.</TextSmall>
                <View sx={{ ...sxStyles.viewColumn, ...{ flex: 1 } }}>
                  <TextSmall sx={{ ...sxStyles.textNote, ...sxStyles.textNoteMargin }}>
                    {t('global-bill-payment::instruction.text--notice-should-the-instruction-rejected')}
                  </TextSmall>
                </View>
              </View>
            </View>
          </View>
        )}
        {/* Later */}
        {scheduleTab === ScheduleBillPayment.FUTURE && (
          <>
            <TextCaption semiBold sx={sxStyles.textScheduleDate}>
              {t('global-bill-payment::glossary.scheduled-date')}
            </TextCaption>
            <DatePickerCallBackComponent
              dateProps={schedule}
              sx={{ borderColor: isErrorScheduleDate ? '$danger' : '$borderButton' }}
              onClick={() => {
                onDateClick(DatePickerType.SCHEDULE_DATE_LATER);
              }}
            />
            {isErrorScheduleDate && (
              <TextCaption danger sx={sxStyles.marginTop4}>
                {billPaymentState.scheduleDate?.message}
              </TextCaption>
            )}
            <View sx={sxStyles.futureNote1}>
              <TextSmall sx={sxStyles.textNumberRecurring}>1.</TextSmall>
              <View sx={{ ...sxStyles.viewColumn, ...{ flex: 1 } }}>
                <TextSmall sx={{ ...sxStyles.textNote, ...sxStyles.textNoteMargin }}>
                  {t('global-bill-payment::instruction.text--notice-should-the-instruction-rejected')}
                </TextSmall>
              </View>
            </View>
            <View sx={sxStyles.futureNote2}>
              <TextSmall sx={sxStyles.textNumberRecurring}>2.</TextSmall>
              <View sx={{ ...sxStyles.viewColumn, ...{ flex: 1 } }}>
                <TextSmall sx={{ ...sxStyles.textNote, ...sxStyles.textNoteMargin }}>
                  {t('global-bill-payment::instruction.text--notice-please-ensure')}
                </TextSmall>
              </View>
            </View>
          </>
        )}
        {/* Recurring */}
        {scheduleTab === ScheduleBillPayment.RECURRING && (
          <View sx={sxStyles.recurringContainer}>
            <View sx={sxStyles.viewTitleIconHorizontal}>
              <TextCaption semiBold sx={sxStyles.textDateOfFirstPayment}>
                {t('global-bill-payment::glossary.date-of-first-payment')}
              </TextCaption>
              <View sx={sxStyles.viewIcon}>
                <Icon name="info" color="#000000" width={15} height={15} />
              </View>
            </View>
            <DatePickerCallBackComponent
              dateProps={dateOfFirstPayment}
              sx={{ borderColor: isErrorDateOfFirstPayment ? '$danger' : '$borderButton' }}
              onClick={() => {
                onDateClick(DatePickerType.DATE_OF_FIRST_PAYMENT);
              }}
            />
            {isErrorDateOfFirstPayment && (
              <TextCaption danger sx={sxStyles.marginTop4}>
                {billPaymentState.dateOfFirstPayment?.message}
              </TextCaption>
            )}
            <View sx={sxStyles.viewTitleIconHorizontal}>
              <TextCaption semiBold sx={sxStyles.textDateOfFirstPayment}>
                {t('global-bill-payment::glossary.frequency')}
              </TextCaption>
              <View sx={sxStyles.viewIcon}>
                <Icon name="info" color="#000000" width={15} height={15} />
              </View>
            </View>
            <View sx={sxStyles.dropdownFrequencyWrapper}>
              <Dropdown
                sx={{ ...sxStyles.dropdown, ...{ borderColor: isErrorInterval ? '$danger' : '$borderButton' } }}
                onValueChange={setInterval}
                options={[
                  {
                    label: t('global-bill-payment::glossary.daily'),
                    value: IntervalBillPayment.DAILY
                  },
                  {
                    label: t('global-bill-payment::glossary.weekly'),
                    value: IntervalBillPayment.WEEKLY
                  },
                  {
                    label: t('global-bill-payment::glossary.monthly'),
                    value: IntervalBillPayment.MONTHLY
                  }
                ]}
                value={interval}
                placeholder={t('global-bill-payment::glossary.select-frequency')}
              />
            </View>
            {isErrorInterval && (
              <TextCaption danger sx={sxStyles.marginTop4}>
                {billPaymentState.interval?.message}
              </TextCaption>
            )}

            <TextCaption semiBold sx={sxStyles.textScheduleDate}>
              {t('global-bill-payment::glossary.payment-end-date')}
            </TextCaption>
            <View sx={{ zIndex: 500 }}>
              <DatePickerCallBackComponent
                dateProps={paymentEndDate}
                sx={{ borderColor: isErrorPaymentEndDate ? '$danger' : '$borderButton' }}
                onClick={() => {
                  onDateClick(DatePickerType.END_DATE_PAYMENT);
                }}
              />
            </View>
            {isErrorPaymentEndDate && (
              <TextCaption danger sx={sxStyles.marginTop4}>
                {billPaymentState.paymentEndDate?.message}
              </TextCaption>
            )}
            <View sx={sxStyles.curringNoteWrapper}>
              <TextSmall sx={sxStyles.textNumberRecurring}>1.</TextSmall>
              <View sx={{ ...sxStyles.viewColumn, ...{ flex: 1 } }}>
                <TextSmall sx={{ ...sxStyles.textNote, ...sxStyles.textNoteMargin }}>
                  {t('global-bill-payment::instruction.text--notice-your-recurring')}
                </TextSmall>
              </View>
            </View>
            <View sx={sxStyles.viewRow}>
              <TextSmall sx={sxStyles.textNumberRecurring}>2.</TextSmall>
              <View sx={{ ...sxStyles.viewColumn, ...{ flex: 1 } }}>
                <TextSmall sx={{ ...sxStyles.textNote, ...sxStyles.textNoteMargin }}>
                  {t('global-bill-payment::instruction.text--notice-please-noted-that')}
                </TextSmall>
              </View>
            </View>
            <View sx={sxStyles.viewRow}>
              <TextSmall sx={sxStyles.textNumberRecurring}>3.</TextSmall>
              <View sx={{ ...sxStyles.viewColumn, ...{ flex: 1 } }}>
                <TextSmall sx={{ ...sxStyles.textNote, ...sxStyles.textNoteMargin }}>
                  {t('global-bill-payment::instruction.text--notice-should-the-instruction-rejected')}
                </TextSmall>
              </View>
            </View>
            <View sx={sxStyles.viewRow}>
              <TextSmall sx={sxStyles.textNumberRecurring}>4.</TextSmall>
              <View sx={{ ...sxStyles.viewColumn, ...{ flex: 1 } }}>
                <TextSmall sx={{ ...sxStyles.textNote, ...sxStyles.textNoteMargin }}>
                  {t('global-bill-payment::instruction.text--notice-if-the-payment')}
                </TextSmall>
              </View>
            </View>
          </View>
        )}
      </View>
    </View>
  );
};

const sxStyles: Record<string, SxProp> = {
  container: {
    flexDirection: 'column',
    width: '100%'
  },
  leftContainer: {
    pl: '$8',
    flexDirection: 'column'
  },
  rightContainer: {
    mt: '$5'
  },
  number: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '$6',
    height: '$6',
    borderRadius: 50,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '$secondary'
  },
  textNumber: {
    pt: 2,
    color: 'white'
  },
  textSchedule: {
    mt: 3,
    mb: 6
  },
  textScheduleDate: {
    mt: '$5',
    mb: '$1'
  },
  textDateOfFirstPayment: {
    mb: '$1',
    mr: '$1'
  },
  textNote: {
    color: '$textSecondary'
  },
  textNoteMargin: {
    ml: '$1'
  },
  dropdownFrequencyWrapper: {
    flex: 1,
    height: '$12',
    zIndex: 8888
  },
  dropdown: {
    width: '100%',
    height: '$12'
  },
  viewTitleIconHorizontal: {
    flexDirection: 'row',
    alignItems: 'center',
    mt: '$5'
  },
  viewIcon: {
    mb: 3
  },
  viewRow: {
    flexDirection: 'row'
  },
  marginLeft4: {
    ml: '$1'
  },
  marginTop4: {
    mt: '$1'
  },
  recurringContainer: {
    mb: '$5'
  },
  curringNoteWrapper: {
    flexDirection: 'row',
    mt: '$5'
  },
  viewRemarkWrapper: {
    my: '$10'
  },
  futureNote1: {
    flexDirection: 'row',
    mt: '$5'
  },
  futureNote2: {
    flexDirection: 'row'
  },
  nowWrapper: {
    mb: '$5'
  },
  marginTop20: {
    mt: '$5'
  },
  textQuestion: {
    pt: '$1'
  },
  viewColumn: {
    flexDirection: 'column'
  },
  textNumberRecurring: {
    color: '$textSecondary',
    width: 12,
    textAlign: 'right'
  }
};
